import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';

// One empty row so toggling the proof on presents a ready-to-type input.
export const DEFAULT_GUEST_LIST = { entries: [{ type: 'poly_user_id', value: '' }] };

export const MAX_GUEST_LIST_ENTRIES = 100;

// The server rejects blank entry values; drop half-filled rows before submit.
export const sanitizeGuestList = (guestList) => ({
  entries: (guestList?.entries || [])
    .map((entry) => ({ ...entry, value: (entry.value || '').trim() }))
    .filter((entry) => entry.value.length > 0),
});

export default function GuestListFields({ value, onChange, disabled = false }) {
  const { t } = useTranslation('shared');
  // Tolerate a partial/empty value (an existing link returns {} from the API).
  const entries = value?.entries || [];
  const atCap = entries.length >= MAX_GUEST_LIST_ENTRIES;
  const hasRealEntry = entries.some((entry) => (entry.value || '').trim());
  // New rows inherit the last row's type: guests usually come as one kind of list.
  const lastType = entries.length ? entries[entries.length - 1].type : 'poly_user_id';

  const inputRefs = useRef([]);
  const [pendingFocus, setPendingFocus] = useState(null);
  useEffect(() => {
    if (pendingFocus !== null) {
      inputRefs.current[pendingFocus]?.focus();
      setPendingFocus(null);
    }
  }, [pendingFocus]);

  const update = (nextEntries) => onChange({ entries: nextEntries });

  const updateEntry = (index, patch) =>
    update(entries.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));

  const removeEntry = (index) => update(entries.filter((_entry, i) => i !== index));

  const addEntry = () => {
    if (atCap) return;
    update([...entries, { type: lastType, value: '' }]);
    setPendingFocus(entries.length);
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key !== 'Enter') return;
    // Keep Enter from submitting the surrounding dialog form.
    event.preventDefault();
    if (!(entries[index].value || '').trim()) return;
    if (index === entries.length - 1) {
      addEntry();
    } else {
      setPendingFocus(index + 1);
    }
  };

  const handlePaste = (index) => (event) => {
    const text = event.clipboardData?.getData('text') || '';
    if (!text.includes('\n')) return;
    event.preventDefault();
    const values = text.split(/\r?\n/).map((v) => v.trim()).filter(Boolean);
    if (!values.length) return;
    const next = entries.map((entry, i) =>
      i === index ? { ...entry, value: values[0] } : entry
    );
    const rest = values.slice(1).map((v) => ({ type: entries[index].type, value: v }));
    next.splice(index + 1, 0, ...rest);
    update(next.slice(0, MAX_GUEST_LIST_ENTRIES));
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
        {t('guestList.helper')}
      </Typography>

      {entries.map((entry, index) => (
        // Wraps at narrow widths: the type select takes its own line and the
        // value input keeps a usable width instead of being crushed.
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            disabled={disabled}
            value={entry.type || 'poly_user_id'}
            onChange={(e) => updateEntry(index, { type: e.target.value })}
            // 200px clears the "Polyguard user ID" label + MUI's arrow gutter;
            // 180 left only a sub-pixel buffer and ellipsized.
            sx={{ minWidth: 200, flexShrink: 0 }}
          >
            <MenuItem value="poly_user_id">{t('guestList.typePolyUserId')}</MenuItem>
            <MenuItem value="full_name">{t('guestList.typeFullName')}</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '1 1 240px' }}>
            <TextField
              size="small"
              fullWidth
              disabled={disabled}
              value={entry.value || ''}
              inputRef={(el) => { inputRefs.current[index] = el; }}
              onChange={(e) => updateEntry(index, { value: e.target.value })}
              onKeyDown={handleKeyDown(index)}
              onPaste={handlePaste(index)}
              placeholder={
                entry.type === 'full_name'
                  ? t('guestList.fullNamePlaceholder')
                  : t('guestList.polyUserIdPlaceholder')
              }
              inputProps={{ maxLength: 200 }}
            />
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => removeEntry(index)}
              aria-label={t('guestList.removeEntry')}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled || atCap}
        onClick={addEntry}
        sx={{ mt: 1, textTransform: 'none' }}
      >
        {t('guestList.addEntry')}
      </Button>

      {!hasRealEntry && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            color: (theme) => theme.palette.warning.main
          }}
        >
          {t('guestList.emptyWarning')}
        </Typography>
      )}
    </Box>
  );
}
